import { useCallback, useMemo } from "react";
import { logger, LogContext, LogLevel } from "./logger";

/**
 * React hook for logging in components
 * Provides a logger instance with component context
 */
export function useLogger(componentName: string) {
  const componentLogger = useMemo(() => {
    return logger.withContext({ component: componentName });
  }, [componentName]);

  const log = useCallback(
    (
      level: LogLevel,
      message: string,
      context?: Omit<LogContext, "component">,
      meta?: any,
    ) => {
      componentLogger[level](message, context, meta);
    },
    [componentLogger],
  );

  const debug = useCallback(
    (message: string, context?: Omit<LogContext, "component">, meta?: any) => {
      componentLogger.debug(message, context, meta);
    },
    [componentLogger],
  );

  const info = useCallback(
    (message: string, context?: Omit<LogContext, "component">, meta?: any) => {
      componentLogger.info(message, context, meta);
    },
    [componentLogger],
  );

  const warn = useCallback(
    (message: string, context?: Omit<LogContext, "component">, meta?: any) => {
      componentLogger.warn(message, context, meta);
    },
    [componentLogger],
  );

  const error = useCallback(
    (message: string, context?: Omit<LogContext, "component">, meta?: any) => {
      componentLogger.error(message, context, meta);
    },
    [componentLogger],
  );

  const apiCall = useCallback(
    (
      method: string,
      url: string,
      status?: number,
      duration?: number,
      context?: Omit<LogContext, "component">,
      meta?: any,
    ) => {
      componentLogger.apiCall(method, url, status, duration, context, meta);
    },
    [componentLogger],
  );

  const graphqlOperation = useCallback(
    (
      operation: string,
      duration?: number,
      context?: Omit<LogContext, "component">,
      meta?: any,
    ) => {
      componentLogger.graphqlOperation(operation, duration, context, meta);
    },
    [componentLogger],
  );

  const userAction = useCallback(
    (action: string, context?: Omit<LogContext, "component">, meta?: any) => {
      componentLogger.userAction(action, context, meta);
    },
    [componentLogger],
  );

  const performance = useCallback(
    (
      operation: string,
      duration: number,
      context?: Omit<LogContext, "component">,
      meta?: any,
    ) => {
      componentLogger.performance(operation, duration, context, meta);
    },
    [componentLogger],
  );

  return {
    log,
    debug,
    info,
    warn,
    error,
    apiCall,
    graphqlOperation,
    userAction,
    performance,
  };
}
